/**
 * Regex tables and matchers that detect AI-authored timestamp mentions in
 * prose. Pure string-in, structured-out — no React, no markdown AST. The
 * remark plugin (see ./remarkTimestampMentions.ts) consumes these matchers.
 *
 * Four wall-clock units are recognized:
 *
 *   t=<digits>ns                → nanoseconds
 *   t=<digits>(µs|μs|us)        → microseconds
 *   t=<digits>ms                → milliseconds
 *   t=<digits>[.<frac>]s        → seconds (lossless via string math)
 *
 * The `t=` + unit envelope is the sole discriminator for prose mentions.
 * Bare digits and any other prefix pass through unchanged — except directly
 * after an enveloped mention, where a `-`, `–`, `—` or ` to ` separator
 * continues it into an interval whose values are all at or after the
 * enveloped one (see {@link matchContinuation}). A descending range
 * (`t=<B>ns-<A>ns`) is deliberately not rescued: its far end reads as a
 * duration.
 */

export interface TimestampMention {
  start: number;
  end: number;
  raw: string;
  ns: bigint;
}

// Digit widths are unconstrained so that clock-broken recordings anchored
// near 1970 still round-trip. Bare decimal numbers in prose ("took 3.4s")
// remain rejected because they lack the `t=` prefix.
const ENVELOPED_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  {
    // ns
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ns\b/g,
    toNs: (intPart) => BigInt(intPart),
  },
  {
    // µs — any of the three micro spellings
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*(?:µs|μs|us)\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000n,
  },
  {
    // ms
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)\s*ms\b/g,
    toNs: (intPart) => BigInt(intPart) * 1_000_000n,
  },
  {
    // s — optional fractional (up to 9 digits) kept lossless
    re: /(?<![A-Za-z0-9_.])[~≈]?\s*t\s*=\s*(\d+)(?:\.(\d{1,9}))?\s*s\b/g,
    toNs: (intPart, fracPart) => {
      const frac = (fracPart ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(intPart) * 1_000_000_000n + BigInt(frac);
    },
  },
];

export function findTimestampMentions(text: string): TimestampMention[] {
  // Every enveloped pattern needs a literal `=`, and continuations are only
  // ever seeded from an enveloped match, so prose without one cannot hold a
  // mention. Rejecting it here skips the four scans on ordinary text nodes.
  if (!text.includes("=")) {
    return [];
  }
  const mentions: TimestampMention[] = [];
  for (const { re, toNs } of ENVELOPED_REGEXES) {
    re.lastIndex = 0;
    for (const m of text.matchAll(re)) {
      const start = m.index ?? 0;
      mentions.push({
        start,
        end: start + m[0].length,
        raw: m[0],
        ns: toNs(m[1], m[2]),
      });
    }
  }
  // Overlap resolution depends on TWO coupled invariants — preserve both
  // when editing this code:
  //   (a) `ENVELOPED_REGEXES` is iterated in declaration order, so the
  //       higher-precision unit (ns first) is pushed before less-specific
  //       overlapping units. Reordering the array would flip precedence.
  //   (b) `Array.prototype.sort` is stable in V8 / modern engines, so on
  //       equal `(start, -end)` keys the first-pushed match wins. Swapping
  //       in `Array.prototype.toSorted` or any unstable sort would silently
  //       break tie-breaking and let lower-precision matches win.
  mentions.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: TimestampMention[] = [];
  let lastEnd = -1;
  for (const m of mentions) {
    if (m.start < lastEnd) {
      continue;
    }
    out.push(m);
    lastEnd = m.end;
    // Each accepted mention may anchor an interval the model wrote under a
    // single envelope; every continuation extends the run. The enveloped
    // value is the floor for the whole run, so a chain need not be
    // monotonic (`t=100ms-300ms-200ms` converts all three).
    for (
      let cont = matchContinuation(text, lastEnd, m.ns);
      cont !== null;
      cont = matchContinuation(text, lastEnd, m.ns)
    ) {
      out.push(cont);
      lastEnd = cont.end;
    }
  }
  return out;
}

/**
 * True when the entire trimmed string is a single `t=<n><unit>` mention.
 */
export function isTimestampMention(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return false;
  }
  const mentions = findTimestampMentions(trimmed);
  return (
    mentions.length === 1 &&
    mentions[0].start === 0 &&
    mentions[0].end === trimmed.length
  );
}

// ---------------------------------------------------------------------------
// Bare-unit mentions (no `t=` prefix)
// ---------------------------------------------------------------------------
// Used to rescue timestamps the AI wrote with the `t=` prefix outside a
// backticked highlight (e.g. ``t=`1771481251863355727ns```). The digits+unit
// half ends up as an inlineCode node; the `t=` preamble sits in the
// preceding text sibling.

const BARE_UNIT_REGEXES: {
  re: RegExp;
  toNs: (intPart: string, fracPart?: string) => bigint;
}[] = [
  { re: /^\s*(\d+)\s*ns\s*$/, toNs: (i) => BigInt(i) },
  { re: /^\s*(\d+)\s*(?:µs|μs|us)\s*$/, toNs: (i) => BigInt(i) * 1_000n },
  { re: /^\s*(\d+)\s*ms\s*$/, toNs: (i) => BigInt(i) * 1_000_000n },
  {
    re: /^\s*(\d+)(?:\.(\d{1,9}))?\s*s\s*$/,
    toNs: (i, f) => {
      const frac = (f ?? "").padEnd(9, "0").slice(0, 9);
      return BigInt(i) * 1_000_000_000n + BigInt(frac);
    },
  },
];

/** Match a unit-only mention (no `t=` prefix) that fills the entire string. */
export function matchBareUnitMention(
  text: string,
): { ns: bigint; raw: string } | null {
  for (const { re, toNs } of BARE_UNIT_REGEXES) {
    const m = text.match(re);
    if (m) {
      return { ns: toNs(m[1], m[2]), raw: text };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Continuations
// ---------------------------------------------------------------------------
// The prompt asks the model to write an interval as two independently
// enveloped timestamps (`t=<A>ns to t=<B>ns`), but it routinely emits one
// envelope spanning both (`t=<A>ns-<B>ns`). The far end is then bare digits,
// which the enveloped pass cannot see: it stays raw epoch nanoseconds beside
// a re-rendered near end, which is how a customer read
// `1786643755.837349000 s-1786643878337759000ns` in one line.
//
// Anchoring on a mention the enveloped pass already accepted keeps the
// bare-digit rule intact everywhere else — a duration in prose ("took 3.4s")
// is still not a timestamp — and lets a chain (`A-B-C`) fall out of the loop
// in findTimestampMentions rather than needing its own shape.

// Shape only, mirroring BARE_UNIT_REGEXES above (a fractional part is legal
// on `s` alone): matchBareUnitMention stays the authority on the value, so a
// unit added to that table has to be added here too. Sticky, so the
// continuation must start exactly where the previous value ended — no
// scanning ahead into unrelated prose, and the span is disjoint from its
// predecessor's by construction (rewriteTextNode splices on those spans).
const CONTINUATION_REGEX =
  /(?:\s*[-–—]\s*|\s+to\s+)(\d+(?:\.\d{1,9})?\s*s|\d+\s*(?:ns|µs|μs|us|ms))\b/y;

/**
 * Match the value continuing an interval at `from` (the end of the previous
 * value), or null if the text there is not one. The first value that fails
 * ends the run; nothing after it is rescued.
 */
function matchContinuation(
  text: string,
  from: number,
  floorNs: bigint,
): TimestampMention | null {
  CONTINUATION_REGEX.lastIndex = from;
  const m = CONTINUATION_REGEX.exec(text);
  const value = m === null ? null : matchBareUnitMention(m[1]);
  // A value before the enveloped anchor is a duration rather than the far end
  // of an interval ("t=<A>ns - 3.4s of skew") and stays untouched; one equal
  // to it is an empty interval and is accepted. This is the only duration
  // guard: a near-zero anchor followed by a duration ("t=5ns - 3.4s of skew")
  // is indistinguishable from a range in a clock-broken recording, and
  // telling them apart would need the epoch-plausibility check this module
  // refuses (see the note above ENVELOPED_REGEXES).
  if (m === null || value === null || value.ns < floorNs) {
    return null;
  }
  const end = CONTINUATION_REGEX.lastIndex;
  return { start: end - m[1].length, end, raw: m[1], ns: value.ns };
}

/**
 * Match a trailing `t\s*=\s*` on a text node, returning the prefix to keep
 * and confirming the suffix existed. The prefix's trailing whitespace is
 * preserved so rewritten output keeps the natural spacing around the
 * mention (`"found "` stays `"found "`). Approximation marks (`~`, `≈`)
 * are consumed with the `t=` token.
 */
export function stripTrailingTEquals(text: string): string | null {
  const m = text.match(/^(.*?)[~≈]?t\s*=\s*$/s);
  return m ? m[1] : null;
}
